/**
 * Phoenix Putter Co — Back in stock ("Email me when it's back")
 * Works with the Impact theme (Maestrooo). No external dependencies.
 *
 * - Shows/hides itself on the theme's `variant:change` event (bubbles from the product <form>).
 * - Submits the native Shopify "customer" form via fetch so the page never reloads.
 *   Shopify creates/updates the customer, subscribes them to email marketing and applies the tags
 *   (back-in-stock, bis-v-<variantId>, bis-p-<handle>). Shopify Flow then emails them on restock.
 * - If Shopify answers with a spam challenge, we fall back to a normal form post so the
 *   customer can complete it.
 */
(function () {
  if (window.PhxBackInStock) return;

  class BackInStock extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('.phx-bis__trigger');
      this.panel = this.querySelector('.phx-bis__panel');
      this.form = this.querySelector('.phx-bis__form');
      this.success = this.querySelector('.phx-bis__success');
      this.message = this.querySelector('.phx-bis__message');
      this.tagsInput = this.querySelector('.phx-bis__tags');
      this.emailInput = this.querySelector('input[type="email"]');

      this._onTrigger = this._onTrigger.bind(this);
      this._onSubmit = this._onSubmit.bind(this);
      this._onVariantChange = this._onVariantChange.bind(this);

      this.trigger && this.trigger.addEventListener('click', this._onTrigger);
      this.form && this.form.addEventListener('submit', this._onSubmit);

      // Impact dispatches variant:change on the product form; it bubbles, so listen at document level.
      document.addEventListener('variant:change', this._onVariantChange);
    }

    disconnectedCallback() {
      document.removeEventListener('variant:change', this._onVariantChange);
    }

    /* ---------- state ---------- */

    _onVariantChange(event) {
      const detail = event.detail || {};

      // Impact passes detail.formId = form.id — but the product form contains <input name="id">,
      // so form.id resolves to that INPUT element, not the string. Resolve the real form id safely.
      let formId = typeof detail.formId === 'string' ? detail.formId : '';
      if (!formId) {
        const formEl = event.target && event.target.closest ? event.target.closest('form') : null;
        formId = formEl ? (formEl.getAttribute('id') || '') : '';
      }
      if (formId && formId !== this.getAttribute('form-id')) return;

      const variant = detail.variant;
      if (!variant) return;

      const variantTitle = variant.title && variant.title !== 'Default Title' ? variant.title : '';
      this.setAttribute('variant-id', variant.id);
      this.setAttribute('variant-title', variantTitle);
      this._updateCopy(variantTitle);
      this._updateTags(variant.id);
      this._reset();

      this.hidden = !!variant.available;
    }

    _updateCopy(variantTitle) {
      const product = this.getAttribute('product-title') || '';
      this.querySelectorAll('.phx-bis__product').forEach((el, i) => {
        // First instance (intro copy) shows the variant; the success copy stays product-only.
        if (i === 0 && variantTitle) {
          el.innerHTML = '';
          el.append(product + ' — ');
          const span = document.createElement('span');
          span.className = 'phx-bis__variant';
          span.textContent = variantTitle;
          el.append(span);
        } else {
          el.textContent = product;
        }
      });
    }

    _updateTags(variantId) {
      if (!this.tagsInput) return;
      const handle = this.getAttribute('product-handle') || '';
      this.tagsInput.value = 'back-in-stock, bis-v-' + variantId + ', bis-p-' + handle;
    }

    _reset() {
      this.classList.remove('phx-bis--loading');
      if (this.form) this.form.hidden = false;
      if (this.success) this.success.hidden = true;
      this._setMessage('');
    }

    _setMessage(text, isError) {
      if (!this.message) return;
      this.message.textContent = text;
      this.message.hidden = !text;
      this.message.classList.toggle('is-error', !!isError);
    }

    /* ---------- open / close ---------- */

    _onTrigger() {
      const open = this.panel.hidden;
      this.panel.hidden = !open;
      this.trigger.setAttribute('aria-expanded', String(open));
      if (open && this.emailInput) {
        setTimeout(() => this.emailInput.focus({ preventScroll: false }), 50);
      }
    }

    /* ---------- submit ---------- */

    async _onSubmit(event) {
      event.preventDefault();

      const email = (this.emailInput && this.emailInput.value || '').trim();
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        this._setMessage('Please enter a valid email address.', true);
        this.emailInput && this.emailInput.focus();
        return;
      }

      this._setMessage('');
      this.classList.add('phx-bis--loading');

      try {
        const body = new FormData(this.form);
        const response = await fetch(this.form.action, {
          method: 'POST',
          body,
          credentials: 'same-origin',
          headers: { 'Accept': 'text/html', 'X-Requested-With': 'XMLHttpRequest' }
        });

        const finalUrl = response.url || '';

        // Shopify's bot-protection page: let the browser submit normally so the user can pass it.
        if (finalUrl.indexOf('/challenge') !== -1) {
          this.form.submit();
          return;
        }

        const html = await response.text();
        const posted = finalUrl.indexOf('customer_posted=true') !== -1 || /customer_posted=true/.test(html);
        const hasErrors = /class="[^"]*(form__errors|errors|form-errors)[^"]*"/i.test(html) && !posted;

        if (response.ok && !hasErrors) {
          this._showSuccess(email);
        } else {
          this._setMessage("Hmm, that didn't go through. Please double-check your email and try again.", true);
        }
      } catch (err) {
        this._setMessage('Network hiccup — please try again in a moment.', true);
      } finally {
        this.classList.remove('phx-bis--loading');
      }
    }

    _showSuccess(email) {
      if (this.form) this.form.hidden = true;
      if (this.success) this.success.hidden = false;

      // Analytics hook (Shopify customer events / GA4 via theme, if wired)
      try {
        document.dispatchEvent(new CustomEvent('phx:back-in-stock:subscribed', {
          bubbles: true,
          detail: {
            email,
            productId: this.getAttribute('product-id'),
            productHandle: this.getAttribute('product-handle'),
            variantId: this.getAttribute('variant-id')
          }
        }));
      } catch (e) { /* noop */ }
    }
  }

  if (!window.customElements.get('back-in-stock')) {
    window.customElements.define('back-in-stock', BackInStock);
  }

  window.PhxBackInStock = true;
})();
